/**
 * Settles: how a body comes to rest, instead of cutting to its standing frame.
 *
 * A body that stops walking or running is caught somewhere in its stride; one that has struck a
 * blow is left in its fighting stance. A settle is a short clip from that pose into the standing
 * one: a foot off its spot steps in (the standing one follows if it is far from its own), the hips
 * rise out of the stride's crouch, the chest rocks and comes upright, and the arms carry on a
 * little with the swing they had and come to rest. Each of those is a damped spring, so the motion
 * keeps the momentum it had and lands without a bounce.
 *
 * And the other way: a blow struck from standing eases its first frames out of the standing pose,
 * the lead foot stepping into the stance, without changing when the blow lands.
 */
import type { EmberClip } from "./clips"
import { ArmPose, GRIP_OUT, LegTarget, PoseSample, Proportions, SIDES, Side, Skeleton, WRIST_REST, maxPelvisHeight, solvePose } from "./rig"
import { Vec3, add, apply, dot, ease, length, lerp, normalize, rotY, scale, smoothstep, sub, transpose } from "./vec"

/** The action a settle's name starts with: `settle.walk.5-left` (from stride frame 5), `settle.slash-left`. */
export const SETTLE = "settle"
/** What a blow struck from standing adds to its name: `slash.rest-left`. */
export const FROM_REST = "rest"
/** Attacks struck from a stance with the lead foot forward: eased into from standing and settled out of. */
export const STANCE_ATTACKS = ["slash", "thrust"]
export const SETTLE_FRAME_MS = 50

export interface SettleTuning {
    /** How long a settle takes with one step, and with two (the standing foot following), in seconds. */
    oneStep: number
    twoSteps: number
    /** A standing foot further than this from its resting place steps too, rather than sliding. */
    slideMax: number
    /** How high a stepping foot lifts, and how far the hips give as the weight comes down. */
    lift: number
    dip: number
    /** Share of the arms' swing speed carried into the settle. */
    armMomentum: number
    /** Forward pitch a sudden stop throws into the chest (rad/s). */
    leanKick: number
    /** Period (s) and damping ratio of the springs the arms, chest and hips come to rest on. */
    period: number
    damping: number
    /** Caught in the air: the share of the settle the foot about to land takes coming down. */
    landing: number
}

const WALK_SETTLE: SettleTuning = {
    oneStep: 0.3,
    twoSteps: 0.42,
    slideMax: 1.6,
    lift: 1.4,
    dip: 0.35,
    armMomentum: 0.55,
    leanKick: 0.5,
    period: 0.4,
    damping: 0.45,
    landing: 0.12,
}

/** By the stride it stops: a run carries more, settles longer and gives more at the knee; it already leans. */
export const STRIDE_SETTLES: Record<string, SettleTuning> = {
    walk: WALK_SETTLE,
    run: { ...WALK_SETTLE, oneStep: 0.4, twoSteps: 0.52, lift: 1.8, dip: 0.6, armMomentum: 0.4, leanKick: 0, period: 0.45 },
}

/** Out of a blow: one step back for the lead foot, the arms coming down. */
export const STANCE_SETTLE: SettleTuning = { ...WALK_SETTLE, oneStep: 0.3, twoSteps: 0.3, lift: 1.2, dip: 0.3, armMomentum: 0, leanKick: 0 }

/** Ground speed (px/s) a stride's arms are taken to be swinging at as it settles: the player's walk and run. */
export const SETTLE_SPEED: Record<string, number> = { walk: 100, run: 160 }

/** Share of a blow struck from standing over which it eases out of the standing pose. */
export const EASE_IN = 0.3

/** An arm swinging back past its rest goes at most this far beyond it. */
const MAX_SWING_PAST = (8 * Math.PI) / 180

const mix = (a: number, b: number, u: number) => a + (b - a) * u

/** A damped spring's displacement `tau` seconds after leaving x0 at v0. */
const spring = (x0: number, v0: number, tau: number, t: SettleTuning) => {
    const omega = (2 * Math.PI) / t.period
    const wd = omega * Math.sqrt(1 - t.damping * t.damping)
    const e = Math.exp(-t.damping * omega * tau)
    return e * (x0 * Math.cos(wd * tau) + ((v0 + t.damping * omega * x0) / wd) * Math.sin(wd * tau))
}

/** Whatever of the spring is left fades out over the last third, so a settle ends exactly at rest. */
const fadeOut = (u: number) => 1 - smoothstep((u - 0.65) / 0.35)

/** `past` beyond the rest it swung through, held to MAX_SWING_PAST however hard it came. */
const capPast = (past: number) => Math.sign(past) * MAX_SWING_PAST * Math.tanh(Math.abs(past) / MAX_SWING_PAST)

/** An angle settling on `rest` from `from`, moving at `velocity` (rad/s) when it started. */
const settleAngle = (from: number, rest: number, velocity: number, tau: number, u: number, t: SettleTuning) => {
    const d = spring(from - rest, velocity, tau, t) * fadeOut(u)
    return rest + (Math.sign(d) !== Math.sign(from - rest) && d !== 0 ? capPast(d) : d)
}

const leg = (pose: PoseSample, side: Side) => pose.legs[side] as LegTarget
const allIk = (...poses: PoseSample[]) => poses.every(pose => SIDES.every(s => pose.legs[s].mode === "ik"))
const offFrom = (a: LegTarget, b: LegTarget) => Math.hypot(a.ankle[0] - b.ankle[0], a.ankle[2] - b.ankle[2])

/** A foot `s` (0..1) of the way through a step from `a` to `b`, lifted `lift` at its height. */
const stepTo = (a: LegTarget, b: LegTarget, s: number, lift: number): LegTarget => {
    const c = Math.min(1, Math.max(0, s))
    const e = ease(c)
    const up = c > 0 && c < 1 ? lift * Math.sin(Math.PI * c) : 0
    const ankle: Vec3 = [mix(a.ankle[0], b.ankle[0], e), mix(a.ankle[1], b.ankle[1], e) + up, mix(a.ankle[2], b.ankle[2], e)]
    return { mode: "ik", ankle, footPitch: mix(a.footPitch, b.footPitch, e), planted: c <= 0 || c >= 1 }
}

/** Keep the pelvis low enough that every foot on the ground is still reached. */
const reachable = (pose: PoseSample, p: Proportions): PoseSample => {
    const sk = solvePose(pose, p)
    let height = pose.root[1]
    for (const s of SIDES) {
        const l = pose.legs[s]
        if (l.mode === "ik" && l.planted !== false) height = Math.min(height, maxPelvisHeight(l.ankle, [sk[s].hip[0], sk[s].hip[2]], p))
    }
    return height === pose.root[1] ? pose : { ...pose, root: [pose.root[0], height, pose.root[2]] }
}

/** The body's turn about the vertical (a blow's hips turn it; nothing a settle starts from rolls or pitches it). */
const yawOf = (pose: PoseSample) => Math.atan2(pose.rootRotation[2], pose.rootRotation[0])

/** The arm as a place for the hand -- from its shoulder, in the chest's frame -- as `pose` puts it. */
const reachOf = (pose: PoseSample, side: Side, p: Proportions, sk?: Skeleton): ArmPose => {
    const arm = pose.arms[side]
    if (arm.reach && arm.pole && arm.grip) return arm
    const solved = sk ?? solvePose(pose, p)
    const toChest = transpose(solved.torsoBasis)
    const limb = solved[side]
    const bend = normalize(sub(limb.elbow, scale(add(limb.shoulder, limb.hand), 0.5)))
    return {
        ...arm,
        reach: arm.reach ?? apply(toChest, sub(limb.hand, limb.shoulder)),
        pole: arm.pole ?? (dot(bend, bend) > 0.5 ? apply(toChest, bend) : [0, 0, -1]),
        grip: arm.grip ?? apply(toChest, limb.grip),
    }
}

/** Along the great circle from `a` toward `b`: `s` past 1 carries on beyond `b`. */
const slerpDirection = (a: Vec3, b: Vec3, s: number): Vec3 => {
    const ua = normalize(a), ub = normalize(b)
    const angle = Math.acos(Math.max(-1, Math.min(1, dot(ua, ub))))
    if (angle < 1e-4) return normalize(lerp(ua, ub, s))
    const k = Math.sin(angle)
    return add(scale(ua, Math.sin((1 - s) * angle) / k), scale(ub, Math.sin(s * angle) / k))
}

/**
 * An arm `s` of the way from how `a` holds it to how `b` does, `s` past 1 swinging on beyond `b`
 * (held to MAX_SWING_PAST). Angles mix as angles; an arm placed by its hand (a polearm carried, both
 * hands on a hilt, a load in the arms) moves its hand round the shoulder instead.
 */
const blendArm = (a: PoseSample, b: PoseSample, side: Side, s: number, p: Proportions, skA?: Skeleton, skB?: Skeleton): ArmPose => {
    const A = a.arms[side], B = b.arms[side]
    if (!A.reach && !B.reach) {
        const angle = (x: number, y: number) => (s > 1 ? y + capPast((s - 1) * (y - x)) : mix(x, y, s))
        return {
            pitch: angle(A.pitch, B.pitch),
            abduct: angle(A.abduct, B.abduct),
            flex: angle(A.flex, B.flex),
            wrist: mix(A.wrist ?? WRIST_REST, B.wrist ?? WRIST_REST, Math.min(1, s)),
            out: mix(A.out ?? GRIP_OUT, B.out ?? GRIP_OUT, Math.min(1, s)),
            open: s < 0.5 ? A.open : B.open,
        }
    }
    const ra = reachOf(a, side, p, skA), rb = reachOf(b, side, p, skB)
    const turn = Math.acos(Math.max(-1, Math.min(1, dot(normalize(ra.reach!), normalize(rb.reach!)))))
    const along = s > 1 && turn > 1e-4 ? 1 + Math.min(s - 1, MAX_SWING_PAST / turn) : s
    return {
        ...(s < 0.5 ? A : B),
        reach: scale(slerpDirection(ra.reach!, rb.reach!, along), mix(length(ra.reach!), length(rb.reach!), Math.min(1, along))),
        pole: normalize(lerp(ra.pole!, rb.pole!, Math.min(1, along))),
        grip: normalize(lerp(ra.grip!, rb.grip!, Math.min(1, along))),
    }
}

/**
 * Settle out of a stride caught at `phase`: the foot that is off the ground, or furthest from its
 * spot, steps in; a standing foot further than `slideMax` from its own steps after it, a nearer one
 * slides home under the weight. Caught in the air (a run's flight), the foot that would land next
 * comes down where it is first. The arms carry on with the swing they had (a share of it), the chest
 * pitches a little further with the sudden stop and rocks back upright.
 *
 * Built for one body and facing (how many frames it takes depends on where the feet were), so
 * sampling ignores the proportions and facing it is handed. Undefined for a stride or a standing
 * pose whose legs are not placed by their feet.
 */
export const settleFromStride = (
    gait: EmberClip,
    phase: number,
    cyclesPerSecond: number,
    rest: EmberClip,
    tuning: SettleTuning,
    p: Proportions,
    facing: string,
): EmberClip | undefined => {
    const from = gait.sample(phase, p, facing)
    const dt = 0.004
    const next = gait.sample((phase + dt * cyclesPerSecond) % 1, p, facing)
    const still = rest.sample(0, p, facing)
    if (!allIk(from, still)) return undefined
    const off = (s: Side) => offFrom(leg(from, s), leg(still, s))
    const planted = SIDES.filter(s => leg(from, s).planted)
    const landsIn = (s: Side) => {
        for (let k = 1; k <= 100; k++) if ((gait.sample((phase + k / 100) % 1, p, facing).legs[s] as LegTarget).planted) return k
        return Infinity
    }
    const landing = planted.length === 0
    const pivot = landing
        ? SIDES.slice().sort((a, b) => landsIn(a) - landsIn(b))[0]
        : planted.slice().sort((a, b) => off(a) - off(b))[0]
    const stepper: Side = pivot === "L" ? "R" : "L"
    const landed: LegTarget = { ...leg(from, pivot), ankle: [leg(from, pivot).ankle[0], leg(still, pivot).ankle[1], leg(from, pivot).ankle[2]], footPitch: 0 }
    const pivotStart = landing ? landed : leg(from, pivot)
    const slideFrom = landing ? tuning.landing + 0.1 : 0.15
    const twoSteps = off(pivot) > tuning.slideMax
    const duration = twoSteps ? tuning.twoSteps : tuning.oneStep
    const frames = Math.max(2, Math.round((duration * 1000) / SETTLE_FRAME_MS))
    const firstEnd = twoSteps ? 0.5 : 0.7
    const lift = (s: Side, share: number) => Math.min(tuning.lift, Math.max(0.5, off(s) * 0.12)) * share
    const velocity = (a: number, b: number) => (b - a) / dt
    const swinging = SIDES.every(s => !from.arms[s].reach && !still.arms[s].reach)
    const skFrom = solvePose(from, p), skStill = solvePose(still, p)

    const sample = (t: number): PoseSample => {
        // Frame 0 follows the stride frame on show; the last comes just before the standing frame.
        const u = (t * (frames - 1) + 1) / (frames + 1)
        const tau = u * duration
        const e = ease(u)
        const share = 1 - spring(1, 0, tau, tuning) * fadeOut(u)
        const legs = {} as PoseSample["legs"]
        legs[stepper] = stepTo(leg(from, stepper), leg(still, stepper), u / firstEnd, lift(stepper, 1))
        legs[pivot] =
            landing && u < tuning.landing
                ? stepTo(leg(from, pivot), landed, u / tuning.landing, 0)
                : twoSteps
                  ? stepTo(pivotStart, leg(still, pivot), (u - firstEnd) / (0.95 - firstEnd), lift(pivot, 0.7))
                  : { ...stepTo(pivotStart, leg(still, pivot), (u - slideFrom) / (0.85 - slideFrom), 0), planted: true }
        const arm = (s: Side): ArmPose => {
            if (!swinging) return blendArm(from, still, s, share, p, skFrom, skStill)
            const a = from.arms[s], b = still.arms[s], n = next.arms[s]
            return {
                pitch: settleAngle(a.pitch, b.pitch, velocity(a.pitch, n.pitch) * tuning.armMomentum, tau, u, tuning),
                abduct: mix(a.abduct, b.abduct, e),
                flex: settleAngle(a.flex, b.flex, velocity(a.flex, n.flex) * tuning.armMomentum, tau, u, tuning),
                wrist: mix(a.wrist ?? WRIST_REST, b.wrist ?? WRIST_REST, e),
                out: mix(a.out ?? GRIP_OUT, b.out ?? GRIP_OUT, e),
                // The hand opens as it comes to rest, mid-settle, while it still moves.
                open: e < 0.5 ? a.open : b.open,
            }
        }
        const lean = settleAngle(from.lean, still.lean, velocity(from.lean, next.lean) + tuning.leanKick, tau, u, tuning)
        return reachable(
            {
                root: [mix(from.root[0], still.root[0], e), mix(from.root[1], still.root[1], e) - tuning.dip * Math.sin(Math.PI * u), mix(from.root[2], still.root[2], e)],
                rootRotation: still.rootRotation,
                lean,
                twist: settleAngle(from.twist, still.twist, velocity(from.twist, next.twist), tau, u, tuning),
                sideBend: mix(from.sideBend, still.sideBend, e),
                // The eyes stay level as the chest rocks.
                headPitch: mix(from.headPitch, still.headPitch, e) - 0.4 * (lean - mix(from.lean, still.lean, e)),
                headYaw: mix(from.headYaw, still.headYaw, e),
                arms: { L: arm("L"), R: arm("R") },
                legs,
                draw: still.draw,
            },
            p,
        )
    }
    return { name: SETTLE, frames, frameMs: SETTLE_FRAME_MS, loop: false, sample }
}

/**
 * Settle out of the last pose of `end` (a blow struck standing): a foot out of its place steps back,
 * the chest comes upright and the arms come down -- arms raised above level coming down a little out
 * to the sides, not across the face -- swinging once past their rest.
 */
export const settleFromPose = (end: EmberClip, rest: EmberClip, p: Proportions, facing: string, tuning: SettleTuning = STANCE_SETTLE): EmberClip | undefined => {
    const from = end.sample(1, p, facing)
    const still = rest.sample(0, p, facing)
    if (!allIk(from, still)) return undefined
    const duration = tuning.oneStep
    const frames = Math.max(2, Math.round((duration * 1000) / SETTLE_FRAME_MS))
    const skFrom = solvePose(from, p), skStill = solvePose(still, p)
    const yaw = yawOf(from)

    const sample = (t: number): PoseSample => {
        const u = (t * (frames - 1) + 1) / (frames + 1)
        const tau = u * duration
        const e = ease(u)
        const share = 1 - spring(1, 0, tau, tuning) * fadeOut(u)
        const legs = {} as PoseSample["legs"]
        for (const s of SIDES) {
            legs[s] = offFrom(leg(from, s), leg(still, s)) > 0.5 ? stepTo(leg(from, s), leg(still, s), (u - 0.1) / 0.5, tuning.lift) : { ...leg(still, s), planted: true }
        }
        const sweep = (s: Side) => 0.45 * Math.max(0, from.arms[s].pitch - Math.PI / 2) * Math.sin(Math.PI * Math.min(1, u * 1.8))
        const arm = (s: Side): ArmPose => {
            const blended = blendArm(from, still, s, share, p, skFrom, skStill)
            return blended.reach ? blended : { ...blended, abduct: mix(from.arms[s].abduct, still.arms[s].abduct, e) + sweep(s) }
        }
        return reachable(
            {
                root: [mix(from.root[0], still.root[0], e), mix(from.root[1], still.root[1], e) - tuning.dip * Math.sin(Math.PI * u), mix(from.root[2], still.root[2], e)],
                rootRotation: yaw ? rotY(yaw * (1 - e)) : still.rootRotation,
                lean: mix(from.lean, still.lean, share),
                twist: mix(from.twist, still.twist, share),
                sideBend: mix(from.sideBend, still.sideBend, e),
                headPitch: mix(from.headPitch, still.headPitch, e),
                headYaw: mix(from.headYaw, still.headYaw, e),
                arms: { L: arm("L"), R: arm("R") },
                legs,
                draw: still.draw,
            },
            p,
        )
    }
    return { name: SETTLE, frames, frameMs: SETTLE_FRAME_MS, loop: false, sample }
}

/** The stance's lead foot: the one the blow puts furthest ahead of where it stands at rest. */
const leadFoot = (stance: PoseSample, still: PoseSample): Side | undefined => {
    const ahead = (s: Side) => leg(stance, s).ankle[2] - leg(still, s).ankle[2]
    const lead: Side = ahead("L") >= ahead("R") ? "L" : "R"
    return ahead(lead) > 1 ? lead : undefined
}

/**
 * A blow struck standing still: the lead foot steps forward into the stance and the other stays
 * where it stood, so one step out of the blow brings the body back to standing. `fromRest` eases the
 * first EASE_IN of it out of the standing pose -- the lead foot stepping in, the arms and chest
 * mixing from standing into the blow's own -- with the same frames and timing, so the blow lands
 * when it always did.
 */
export const standingAttack = (attack: EmberClip, rest: EmberClip, fromRest: boolean): EmberClip => ({
    ...attack,
    sample: (t, p, facing) => {
        const a = attack.sample(t, p, facing)
        const still = rest.sample(0, p, facing)
        const stance = attack.sample(0, p, facing)
        if (!allIk(a, still, stance)) return a
        const lead = leadFoot(stance, still)
        const into = (t + 0.1) / (EASE_IN + 0.1)
        const legs = {} as PoseSample["legs"]
        for (const s of SIDES) {
            legs[s] = s !== lead ? { ...leg(still, s), planted: true } : fromRest ? stepTo(leg(still, s), leg(a, s), into, STANCE_SETTLE.lift) : leg(a, s)
        }
        const w = fromRest ? smoothstep(into) : 1
        if (w >= 1) return reachable({ ...a, legs }, p)
        return reachable(
            {
                ...a,
                root: [mix(still.root[0], a.root[0], w), mix(still.root[1], a.root[1], w), mix(still.root[2], a.root[2], w)],
                lean: mix(still.lean, a.lean, w),
                twist: mix(still.twist, a.twist, w),
                sideBend: mix(still.sideBend, a.sideBend, w),
                headPitch: mix(still.headPitch, a.headPitch, w),
                headYaw: mix(still.headYaw, a.headYaw, w),
                arms: { L: blendArm(still, a, "L", w, p), R: blendArm(still, a, "R", w, p) },
                legs,
            },
            p,
        )
    },
})
